import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { showUndoToast } from '@/src/renderer/components/common/UndoToast'
import { db } from '../../../lib/db/indexeddb'
import { scheduleSync } from '../../../lib/sync/sync-engine'
import type { Task, Section } from '../../../lib/db/models'

interface RenderSegment {
  section: Section | null
  tasks: Task[]
  taskCount: number
}

interface UseProjectSectionsParams {
  projectId: string | undefined
  tasks: Task[] | undefined
  sections: Section[] | undefined
  segments: RenderSegment[]
  /** Optional optimistic-update sink. When provided, every mutation
   *  here calls it BEFORE the IDB write so the change is visible in
   *  the next paint instead of waiting on the LiveQuery refire. */
  setOptimisticSectionOverride?: (sectionId: string, partial: Partial<Section>) => void
  /** Optional optimistic-update sink for tasks. Used by delete-undo to
   *  re-link tasks to their section without waiting on Dexie. */
  setOptimisticTaskOverride?: (taskId: string, partial: Partial<Task>) => void
}

export function useProjectSections({
  projectId, tasks, sections, segments,
  setOptimisticSectionOverride, setOptimisticTaskOverride,
}: UseProjectSectionsParams) {
  const { t } = useTranslation()
  // Newly created section ID — triggers inline edit on mount.
  // Cleared only after useLiveQuery has delivered the section into segments.
  const [autoEditSectionId, setAutoEditSectionId] = useState<string | null>(null)
  const toggleCollapseRef = useRef<((sectionId: string) => void) | null>(null)

  // Clear autoEditSectionId only after the section is visible in segments,
  // ensuring the SectionHeader mounts with autoEdit=true.
  useEffect(() => {
    if (!autoEditSectionId) return undefined
    const sectionVisible = segments.some(seg => seg.section?.id === autoEditSectionId)
    if (!sectionVisible) return undefined
    // Clear next tick — SectionHeader has already mounted with autoEdit=true
    const timer = setTimeout(() => setAutoEditSectionId(null), 0)
    return () => clearTimeout(timer)
  }, [autoEditSectionId, segments])

  const handleAddSection = useCallback(async (position: 'top' | 'bottom' = 'bottom', anchorId?: string, placement?: 'before' | 'after') => {
    if (!projectId) return

    const allItems = [
      ...(tasks ?? []).map(t => ({ id: t.id, order: t.order })),
      ...(sections ?? []).map(s => ({ id: s.id, order: s.order })),
    ].sort((a, b) => a.order - b.order)

    let order: number
    if (anchorId && placement) {
      const anchorIdx = allItems.findIndex(item => item.id === anchorId)
      if (anchorIdx !== -1) {
        if (placement === 'before') {
          const prev = anchorIdx > 0 ? allItems[anchorIdx - 1] : null
          order = prev
            ? (prev.order + allItems[anchorIdx].order) / 2
            : allItems[anchorIdx].order - 1
        } else {
          const next = anchorIdx < allItems.length - 1 ? allItems[anchorIdx + 1] : null
          order = next
            ? (allItems[anchorIdx].order + next.order) / 2
            : allItems[anchorIdx].order + 1
        }
      } else {
        // Anchor not found — fall back to absolute position
        order = position === 'top'
          ? (allItems.length > 0 ? allItems[0].order - 1 : 0)
          : (allItems.length > 0 ? allItems[allItems.length - 1].order + 1 : 0)
      }
    } else if (position === 'top') {
      order = allItems.length > 0 ? allItems[0].order - 1 : 0
    } else {
      order = allItems.length > 0 ? allItems[allItems.length - 1].order + 1 : 0
    }

    const newSection = await db.createSection({
      title: '',
      projectId,
      order,
    })
    setAutoEditSectionId(newSection.id)
    scheduleSync()
  }, [projectId, tasks, sections])

  const handleToggleCollapse = useCallback(async (sectionId: string) => {
    // Look up the current state from the in-memory list to avoid an
    // extra Dexie roundtrip — the optimistic flip needs to fire BEFORE
    // any await so the next paint shows the new collapsed state.
    const sectionsNow = sections ?? []
    const current = sectionsNow.find(s => s.id === sectionId)
    if (!current) {
      // Fall back to the slower but always-correct path.
      const section = await db.getSection(sectionId)
      if (!section) return
      setOptimisticSectionOverride?.(sectionId, { collapsed: !section.collapsed })
      await db.updateSection(sectionId, { collapsed: !section.collapsed })
      scheduleSync()
      return
    }
    setOptimisticSectionOverride?.(sectionId, { collapsed: !current.collapsed })
    await db.updateSection(sectionId, { collapsed: !current.collapsed })
    scheduleSync()
  }, [sections, setOptimisticSectionOverride])
  toggleCollapseRef.current = handleToggleCollapse

  const handleRenameSection = useCallback(async (sectionId: string, title: string) => {
    setOptimisticSectionOverride?.(sectionId, { title })
    await db.updateSection(sectionId, { title })
    scheduleSync()
  }, [setOptimisticSectionOverride])

  const handleDeleteSection = useCallback(async (sectionId: string) => {
    // Snapshot section + affected task IDs for undo
    const section = await db.getSection(sectionId)
    const affectedTasks = await db.tasks
      .where('sectionId')
      .equals(sectionId)
      .filter(t => !t.deletedAt)
      .toArray()
    const affectedTaskIds = affectedTasks.map(t => t.id)

    // Optimistic delete: drops the section out of the rendered segments
    // (the page filter excludes sections with `deletedAt` set).
    setOptimisticSectionOverride?.(sectionId, { deletedAt: new Date() })

    await db.deleteSection(sectionId)

    // Show undo toast only for named sections (unnamed = auto-cleanup, no toast)
    if (section?.title) {
      showUndoToast({
        message: t('section.deleted', { title: section.title }),
        undoLabel: t('common.undo'),
        onUndo: async () => {
          // Remove pending 'delete' queue entries so sync doesn't re-delete on cloud
          const pendingDeletes = await db.syncQueue
            .filter(q => q.table === 'sections' && q.action === 'delete' && q.data?.localId === sectionId)
            .toArray()
          for (const entry of pendingDeletes) {
            if (entry.id != null) await db.syncQueue.delete(entry.id)
          }

          // Optimistic restore: clear `deletedAt` on the section and
          // re-link the affected tasks. The Dexie writes still run
          // immediately after, but the UI already reflects the undo.
          if (section) setOptimisticSectionOverride?.(section.id, { deletedAt: null })
          for (const taskId of affectedTaskIds) {
            setOptimisticTaskOverride?.(taskId, { sectionId })
          }

          // Restore section
          if (section) {
            await db.sections.put({ ...section, deletedAt: null, synced: false })
            await db.queueSyncChange(
              section.cloudId ? 'update' : 'create',
              'sections',
              { localId: section.id, cloudId: section.cloudId }
            )
          }
          // Re-link tasks to section
          for (const taskId of affectedTaskIds) {
            await db.tasks.update(taskId, { sectionId, synced: false })
            await db.queueSyncChange('update', 'tasks', { localId: taskId })
          }
          scheduleSync()
        },
      })
    }

    scheduleSync()
  }, [t, setOptimisticSectionOverride, setOptimisticTaskOverride])

  return {
    autoEditSectionId,
    handleAddSection,
    handleToggleCollapse,
    handleRenameSection,
    handleDeleteSection,
    toggleCollapseRef,
  }
}
